import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import authorizeLoginButtonClick from '@hh.ru/analytics-js-events/build/career_platform/authorization/authorize_login_button_click';
import authorizeLogoutButtonClick from '@hh.ru/analytics-js-events/build/career_platform/authorization/authorize_logout_button_click';
import { translation, useSelector, TranslatedComponent } from '@hh.ru/front-static-app';
import { BottomSheet, BottomSheetFooter, Button, Divider, Drop, Text, useBreakpoint } from '@hh.ru/magritte-ui';
import { LineArrowRightOutlinedSize24, UserCircleOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { SPALink } from '@hh.ru/redux-spa-middleware';

import { getLogoutRoute, INDEX_ROUTE, LK_ROUTE, LOGIN_ROUTE, PROFILE_ROUTE } from 'src/app/routesUtils';
import ProfileAvatar from 'src/components/ProfileAvatar';
import useFeatureEnabled from 'src/hooks/useFeatureEnabled';
import { analytics, Goal } from 'src/utils/analytics';

import styles from './styles.less';

const Features = {
    isEnabledProfile: 'enabled_profile',
};

const TrlKeys = {
    login: 'career-platform.components.header.login-button.login',
    logout: 'career-platform.components.header.login-button.logout',
    close: 'career-platform.components.header.login-button.close',
    profile: 'career-platform.page-names.profile',
};

const HeaderLoginButton: TranslatedComponent = ({ trls }) => {
    const profile = useSelector((state) => state.profile);
    const isEnabledProfile = useFeatureEnabled(Features.isEnabledProfile);
    const pathname = useSelector((state) => state.router.location.pathname);

    const [isOpenMenu, setIsOpenMenu] = useState(false);
    const activatorRef = useRef<HTMLButtonElement>(null);

    const { isMobile } = useBreakpoint();

    const handleClickLogin = useCallback(() => {
        authorizeLoginButtonClick();
        analytics.reach(Goal.click_authorization_login);
    }, []);

    const handleClickLogout = useCallback(() => {
        authorizeLogoutButtonClick();
        analytics.reach(Goal.click_authorization_logout);
    }, []);

    const handleToggleMenu = useCallback(() => setIsOpenMenu((currentState) => !currentState), []);

    const userAvatar = useMemo(
        () =>
            profile ? (
                <ProfileAvatar
                    size={isMobile ? 24 : 40}
                    onClick={handleToggleMenu}
                    Element="button"
                    ref={activatorRef}
                />
            ) : undefined,
        [handleToggleMenu, isMobile, profile]
    );

    const LOGOUT_ROUTE_OVERRIDE = useMemo(
        () => getLogoutRoute([LK_ROUTE, PROFILE_ROUTE].includes(pathname) ? INDEX_ROUTE : undefined),
        [pathname]
    );

    const menuContent = useMemo(
        () =>
            profile ? (
                <div className={styles.menu}>
                    <Text typography="label-2-regular">{`${profile.firstName} ${profile.lastName}`}</Text>
                    <Divider />
                    {isEnabledProfile && (
                        <Button
                            mode="tertiary"
                            style="neutral"
                            size="large"
                            stretched
                            icon={<UserCircleOutlinedSize24 />}
                            Element={SPALink}
                            to={PROFILE_ROUTE}
                            isSeoLink
                        >
                            {trls[TrlKeys.profile]}
                        </Button>
                    )}
                    <Button
                        mode="tertiary"
                        style="negative"
                        size="large"
                        stretched
                        icon={<LineArrowRightOutlinedSize24 />}
                        Element={SPALink}
                        to={LOGOUT_ROUTE_OVERRIDE}
                        isSeoLink
                        onClick={handleClickLogout}
                    >
                        {trls[TrlKeys.logout]}
                    </Button>
                </div>
            ) : null,
        [handleClickLogout, isEnabledProfile, profile, trls, LOGOUT_ROUTE_OVERRIDE]
    );

    return (
        <Fragment>
            {!profile && (
                <Button
                    mode="secondary"
                    style="neutral"
                    size="small"
                    Element={SPALink}
                    to={LOGIN_ROUTE}
                    isSeoLink
                    onClick={handleClickLogin}
                >
                    {trls[TrlKeys.login]}
                </Button>
            )}
            {profile && (
                <Fragment>
                    {userAvatar}
                    {isMobile && (
                        <BottomSheet
                            visible={isOpenMenu}
                            onClose={handleToggleMenu}
                            footer={
                                <BottomSheetFooter>
                                    <Button
                                        mode="secondary"
                                        style="neutral"
                                        size="large"
                                        stretched
                                        onClick={handleToggleMenu}
                                    >
                                        {trls[TrlKeys.close]}
                                    </Button>
                                </BottomSheetFooter>
                            }
                        >
                            <div className={styles.mobileMenuContainer}>{menuContent}</div>
                        </BottomSheet>
                    )}
                    {!isMobile && (
                        <Drop
                            activatorRef={activatorRef}
                            visible={isOpenMenu}
                            onClose={handleToggleMenu}
                            placement="bottom-right"
                        >
                            <div className={styles.desktopMenuContainer}>{menuContent}</div>
                        </Drop>
                    )}
                </Fragment>
            )}
        </Fragment>
    );
};

export default translation(HeaderLoginButton);
